/**
 * Preview Bridge — postMessage helpers for the Visual Builder ↔ preview
 * iframes.
 *
 * The Super Admin builder posts the current in-progress UI blob to the
 * iframe on every edit (already debounced by the caller via React batch
 * updates). The consumer iframe (Home / Shop / etc.) listens for these
 * messages via `subscribePreview` and hot-swaps its runtime UI blob so
 * changes appear immediately WITHOUT hitting the network and WITHOUT
 * persisting anything.
 *
 * Persistence is only triggered by "Save & Apply" or "Publish" in the
 * builder — never by postMessage traffic.
 *
 * Message contract:
 *   { source:"babubaazar:vb", version:1, type:"preview:update", platform, blob }
 *   { source:"babubaazar:vb", version:1, type:"preview:ping" }
 *
 * Origin safety: consumers ignore any message whose `source` !== the
 * expected sentinel. Because both the builder and the preview iframe are
 * served from the same origin (frontend host), we do not need cross-
 * origin ACLs. When embedding third-party origins in the future, add an
 * explicit allow-list at both sender and receiver.
 */

export const VB_MESSAGE_SOURCE = "babubaazar:vb";
export const VB_MESSAGE_VERSION = 1;

// URL query flag the builder appends to preview iframes so consumers know
// they are running inside the visual builder (used for opt-in behaviours
// like listener wiring + disabling analytics).
export const VB_PREVIEW_FLAG = "vb_preview";

/**
 * Called from the BUILDER. Posts a fresh blob to the given iframe window.
 * Safe to call on every keystroke — React batching + preview iframe's
 * React reconciliation keep it cheap.
 *
 * @param {Window|null} win       Iframe.contentWindow
 * @param {string} platform       "website" | "customer_app" | "store_admin" | "pos"
 * @param {object} blob           Current { sections, components, global_style, card, homepage, header, pdp, ... }
 */
export function postPreviewUpdate(win, platform, blob) {
  if (!win) return;
  try {
    win.postMessage(
      { source: VB_MESSAGE_SOURCE, version: VB_MESSAGE_VERSION, type: "preview:update", platform, blob },
      "*",
    );
  } catch (_e) { /* iframe cross-origin — ignore */ }
}

/**
 * Called from the CONSUMER (customer site, admin dashboard, etc.). Sets
 * up a window "message" listener and returns an `unsubscribe` fn.
 *
 * @param {(payload:{platform:string,blob:object})=>void} onUpdate
 * @returns {()=>void} unsubscribe
 */
export function subscribePreview(onUpdate) {
  if (typeof window === "undefined") return () => {};
  const handler = (evt) => {
    const data = evt?.data;
    if (!data || data.source !== VB_MESSAGE_SOURCE) return;
    if (data.type !== "preview:update") return;
    try {
      onUpdate({ platform: data.platform, blob: data.blob || {} });
    } catch (_e) { /* swallow — preview must never break the consumer */ }
  };
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

/**
 * Detects if the current window is being rendered inside the builder
 * preview iframe. Consumers use this to opt into postMessage listening
 * and to skip heavy analytics / login redirects.
 */
export function isPreviewMode() {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get(VB_PREVIEW_FLAG) === "1") return true;
    // Also true when running inside an iframe served by the same origin.
    return window.self !== window.top;
  } catch { return false; }
}

/**
 * Append the preview flag to a URL string so the iframe consumer opts
 * into postMessage listening.
 */
export function withPreviewFlag(url) {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${VB_PREVIEW_FLAG}=1`;
}
